import {
    createLogPrefix,
    logError,
    type LogTarget,
    noop,
    type Unsubscribe,
} from '@amazon/vinyl-util'
import { type ObservableValue } from '@amazon/vinyl-observable'
import { AuthStatus } from '@amzn/bf-playback'
import { getPlaybackDeps } from '@/blackbolt/playback'
import { clearFeedbackOverlay } from '@/blackbolt/feedback'
import { clearMessageFeedbackOverlay } from '@/blackbolt/chat/messageFeedbackService'
import { clearFollowStateOverlay } from '@/blackbolt/followStateOverlay'
import { FEATURE_GATE_STORAGE_KEYS } from '@/core/featureGate'
import { getCacheManager } from '@/core/offlineCacheManager'
import { navigateTo } from '@/router/router'
import { runSignOutClears } from '@/signOutOverlays'

const target: LogTarget = { logPrefix: createLogPrefix('teardown') }

/**
 * Full sign-out cleanup for the shared-desktop case. Sign-out itself is
 * decoupled: `getAuth().signOut()` runs the session-end hooks while still
 * signed in (playback clears and reports ENDED and STOPPED there), drops the
 * token, and the app bootstrap's auth-state listener (`installSignOutTeardown`)
 * invokes this on the signed-in → signed-out transition. Dropping the auth
 * token alone leaves the previous account's persisted state (session/local
 * storage, cached payloads) live, so the next sign-in on the same machine sees
 * stale content. Beyond the sign-out this also:
 *   1. stops playback + drops the active queue where no hook did (a bridged
 *      host; clearing the playlist controller nulls `current`, which unloads
 *      the shared player),
 *   2. wipes session + local storage so no per-account preference or persisted
 *      token bleeds into the next account on a shared machine, and drops the
 *      in-memory feedback / follow-state overlays (which back those localStorage
 *      keys but survive a `localStorage.clear()` in their own `Map`, so the wipe
 *      alone wouldn't reset them within an SPA sign-out),
 *   3. clears the offline CacheManager (the single instance backs both the
 *      GQL response cache and cached offline files) so the next account never
 *      reads the prior customer's cached payloads,
 *   4. drops the `chat` IndexedDB database (durable chat transcripts, which
 *      the storage clears above don't touch) so no conversation bleeds into
 *      the next account,
 *   5. routes home so no per-account deep link survives the switch.
 *
 * `localStorage.clear()` also drops the device-level preference keys
 * (`explicitLanguageFilter` content filter, `reduceMotion` a11y, `theme`);
 * those losses are intended — a shared machine should not carry one account's
 * device preferences into the next. `deviceInfo` is preserved below: it is
 * this browser's device identity, not an account preference, and regenerating
 * it on every sign-out would present as a brand-new device to Firefly and the
 * DM messaging backend on the very next boot. Feature-gate overrides
 * (`featureGate.*`) are preserved for the same reason: a debug gate is a
 * device/dev preference, not per-account data, so a sign-out triggered while
 * applying a gate change (see `FeatureGatesSection`) must not silently revert
 * it to the shipped default.
 *
 * Every step is independently guarded and best-effort: a throw in any one must
 * not skip the others, and `navigateTo('/')` must always be reached, so each
 * step is wrapped rather than relying on ordering. Idempotent — the auth
 * listener fires it once per sign-out, and every step is safe to re-run.
 */
export function teardown(): void {
    try {
        // Clearing the playlist controller nulls `current`, unloading the
        // shared player; the bridged controller can throw on a closed port.
        getPlaybackDeps()?.playlistController.clear()
    } catch (e) {
        // Playback unavailable — continue teardown.
        logError(target, e)
    }

    wipePersistedState().catch((e) => logError(target, e))

    // The feedback / follow overlays keep an in-memory Map that outlives
    // `localStorage.clear()` on an SPA sign-out, so clear them explicitly.
    try {
        clearFeedbackOverlay()
    } catch (e) {
        // Overlay unavailable — continue teardown.
        logError(target, e)
    }
    try {
        clearMessageFeedbackOverlay()
    } catch (e) {
        // Overlay unavailable — continue teardown.
        logError(target, e)
    }
    try {
        clearFollowStateOverlay()
    } catch (e) {
        // Overlay unavailable — continue teardown.
        logError(target, e)
    }

    // Drop the in-memory half of every persisted optimistic overlay (track /
    // album / artist likes+follows). `localStorage.clear()` above wipes their
    // durable half, but each overlay's in-memory Map outlives it on an SPA
    // sign-out. Decoupled via a registry so teardown imports no domain module
    // (avoids an `auth ↔ track` biref); each is independently guarded.
    runSignOutClears()

    navigateTo('/')
}

/**
 * Deletes everything this browser persisted for the signed-out account: session and local storage
 * (keeping only the device-level keys below), the chat and Bedrock IndexedDB transcripts, and the
 * offline cache. The storage clears run synchronously; the returned promise settles once the
 * database deletes and the cache clear have, and never rejects (each failure is logged).
 */
export function wipePersistedState(): Promise<void> {
    try {
        sessionStorage.clear()
    } catch (e) {
        logError(target, e)
    }
    // Device-level state, not account data: the invite unlock (or sign-out would re-gate an
    // accepted invite), this browser's device identity, and the persisted feature-gate overrides.
    const preservedKeys = ['amuInviteAccepted', 'deviceInfo', ...FEATURE_GATE_STORAGE_KEYS]
    const preserved = new Map<string, string>()
    for (const key of preservedKeys) {
        try {
            const value = localStorage.getItem(key)
            if (value != null) preserved.set(key, value)
        } catch (e) {
            logError(target, e)
        }
    }
    try {
        // The whole keyspace, known keys or not, so nothing of the account survives on a shared PC.
        localStorage.clear()
    } catch (e) {
        logError(target, e)
    }
    for (const [key, value] of preserved) {
        try {
            localStorage.setItem(key, value)
        } catch (e) {
            logError(target, e)
        }
    }

    const logged = (work: () => Promise<unknown>): Promise<unknown> =>
        Promise.resolve()
            .then(work)
            .catch((e) => logError(target, e))
    return Promise.all([
        // Dynamically imported so `idb` stays out of the boot bundle.
        logged(async () => (await import('@/blackbolt/chat/chatStore')).deleteDatabase()),
        logged(async () =>
            (await import('@/bedrockAgent/bedrockTranscriptStore')).deleteDatabase()
        ),
        logged(() => getCacheManager().clear()),
    ]).then(noop)
}

/**
 * Bind sign-out teardown to auth state: run `onSignedOut` (defaults to
 * `teardown`) on the signed-in → signed-out transition only. Guarding on the
 * prior status being `SIGNED_IN` skips both the initial anon boot
 * (`PENDING → SIGNED_OUT`) and any repeat `SIGNED_OUT` emit, so teardown runs
 * exactly once per sign-out.
 */
export function installSignOutTeardown(
    status$: ObservableValue<AuthStatus>,
    onSignedOut: () => void = teardown
): Unsubscribe {
    let previous = status$.value
    return status$.onData((status) => {
        if (status === AuthStatus.SIGNED_OUT && previous === AuthStatus.SIGNED_IN) onSignedOut()
        previous = status
    })
}
